import { useState, useEffect, useCallback } from "react";
import { AmiraResources } from "@/models/AmiraResources.interface";
import { AssignmentManifest } from "@/models/Assignment";
import { getManifestByResourceType } from "@/utilities/WeeklyPlanSliceHelpers";
import { transformId } from "@/utilities/transformId";

interface StudentManifest {
  studentId: string;
  manifest: AssignmentManifest[];
}

export function useAssignmentManagement(students: any[], skillId: string) {
  const [newAssignments, setNewAssignments] = useState<AmiraResources[]>([]);
  const [studentAssignmentsManifest, setStudentAssignmentsManifest] = useState<StudentManifest[]>([]);
  const [removingCurrentAssignments, setRemovingCurrentAssignments] = useState<AmiraResources[]>([]);

  useEffect(() => {
    const initialManifests = students.map(student => {
      const existingManifest = student.assignment?.manifest || [];
      const simplifiedExistingManifest = existingManifest
        .filter((entry: any) => 
          // Only include assignments for the current skill to prevent cross-skill contamination
          entry.skills?.some((skill: any) => skill.skillId === skillId)
        )
        .map((entry: any) => ({
          storyId: entry.storyId,
          activityType: entry.activityType,
          skills: entry.skills?.map((skill: any) => ({ skillId: skill.skillId })) || [],
        }));



      return {
        studentId: student.student.id,
        manifest: simplifiedExistingManifest
      };
    });
    setStudentAssignmentsManifest(initialManifests);
  }, [students, skillId]);

  const addAssignmentsHandler = useCallback((resource: AmiraResources) => {
    setNewAssignments(prev => [...prev, resource]);
    const manifestEntries = getManifestByResourceType(resource, skillId);
    if (!manifestEntries || manifestEntries.length === 0) return;

    const simplifiedManifestEntries = manifestEntries.map(entry => ({
      storyId: entry.storyId,
      activityType: entry.activityType,
      skills: entry.skills?.map(skill => ({ skillId: skill.skillId })),
    }));

    setStudentAssignmentsManifest(prevManifests =>
      prevManifests.map(studentManifest => ({
        ...studentManifest,
        manifest: [...(studentManifest.manifest || []), ...simplifiedManifestEntries]
      }))
    );
  }, [skillId]);

  const removeAssignmentHandler = useCallback((resource: AmiraResources) => {
    setNewAssignments(prev => prev.filter(res => res.resourceUniqueId !== resource.resourceUniqueId)); // Adjusted to check resourceUniqueId

    const manifestEntriesToRemove = getManifestByResourceType(resource, skillId);
    if (!manifestEntriesToRemove.length) return;

    const storyIdsToRemove = new Set(manifestEntriesToRemove.map(entry => entry.storyId));
    const normalizedStoryIdsToRemove = new Set(
        Array.from(storyIdsToRemove).map(id => transformId(id))
    );


    setStudentAssignmentsManifest(prevManifests =>
      prevManifests.map(studentManifest => {
        const updatedManifest = (studentManifest.manifest || []).filter(manifestItem =>
          !storyIdsToRemove.has(manifestItem.storyId) &&
          !normalizedStoryIdsToRemove.has(transformId(manifestItem.storyId))
        );
        return { ...studentManifest, manifest: updatedManifest };
      })
    );
  }, [skillId]);

  const removeCurrentAssignmentHandler = useCallback((resource: AmiraResources) => {
    setRemovingCurrentAssignments(prev => [...prev, resource]);

    const manifestEntriesToRemove = getManifestByResourceType(resource, skillId);
    if (!manifestEntriesToRemove.length) return;

    const storyIdsToRemove = new Set(manifestEntriesToRemove.map(entry => entry.storyId));
     const normalizedStoryIdsToRemove = new Set(
        Array.from(storyIdsToRemove).map(id => transformId(id))
    );


    setStudentAssignmentsManifest(prevManifests =>
      prevManifests.map(studentManifest => ({
        ...studentManifest,
        manifest: (studentManifest.manifest || []).filter(manifestItem =>
          !storyIdsToRemove.has(manifestItem.storyId) &&
          !normalizedStoryIdsToRemove.has(transformId(manifestItem.storyId))
        )
      }))
    );
  }, [skillId]);

  const undoRemoveCurrentAssignment = useCallback((resource: AmiraResources) => {
    setRemovingCurrentAssignments(prev => prev.filter(item => item.resourceUniqueId !== resource.resourceUniqueId));

    const manifestEntriesToAdd = getManifestByResourceType(resource, skillId);
    if (!manifestEntriesToAdd.length) return;

     const simplifiedManifestEntries = manifestEntriesToAdd.map(entry => ({
        storyId: entry.storyId,
        activityType: entry.activityType,
        skills: entry.skills?.map(skill => ({ skillId: skill.skillId })),
    }));


    setStudentAssignmentsManifest(prevManifests =>
      prevManifests.map(studentManifest => ({
        ...studentManifest,
        manifest: [...(studentManifest.manifest || []), ...simplifiedManifestEntries] // Use simplified entries
      }))
    );
  }, [skillId]);

  return {
    newAssignments,
    studentAssignmentsManifest,
    removingCurrentAssignments,
    addAssignmentsHandler,
    removeAssignmentHandler,
    removeCurrentAssignmentHandler,
    undoRemoveCurrentAssignment,
    setStudentAssignmentsManifest // Expose setter if needed externally, though ideally not
  };
}